import { AxiosError } from "axios";
import { useEffect } from "react";
import { privateApiClient, publicApiClient } from "../ApiClient";
import useAuth from "./useAuth";
import useRefreshToken from "./useRefreshToken";
import { jwtDecode } from "jwt-decode";

/** Margines (w sekundach) przed realnym wygaśnięciem access tokenu —
 *  jeśli token wygasa w mniejszym czasie, proaktywnie odświeżamy przed
 *  wysłaniem requestu. Dzięki temu unikamy 401 + retry, które dla
 *  uploadów FormData nie działa (body jest strumieniem, nie da się go
 *  odtworzyć dla drugiego requestu). */
const TOKEN_REFRESH_BUFFER_SEC = 30;

export const useApiClient = () => {
  const { auth, logout } = useAuth();
  const refresh = useRefreshToken();

  useEffect(() => {
    const onError = (error: AxiosError) => {
      if (error.code === AxiosError.ERR_NETWORK) {
        // TODO
      }
    };

    const privateApiClientRequestIntercept = privateApiClient.interceptors.request.use(
      async (config) => {
        config.headers = config.headers ?? {};
        let accessToken = auth.accessToken;

        // Proaktywny refresh: jeśli token wygasa w < TOKEN_REFRESH_BUFFER_SEC,
        // odśwież ZANIM wyślemy request. Kluczowe dla uploadów FormData —
        // retry po 401 nie zadziała bo body jest strumieniem.
        if (accessToken) {
          try {
            const payload = jwtDecode<{ exp?: number }>(accessToken);
            const expiresInSec = (payload?.exp ?? 0) - Math.floor(Date.now() / 1000);
            if (expiresInSec < TOKEN_REFRESH_BUFFER_SEC) {
              try {
                accessToken = await refresh();
              } catch {
                // refresh padł — zostaw stary token, backend zwróci 401,
                // response interceptor obsłuży logout.
              }
            }
          } catch {
            // Nie udało się zdekodować — zostawiamy token jaki jest.
          }
        }

        if (accessToken && !config.headers["Authorization"]) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    const privateApiClientResponseIntercept = privateApiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const prevRequest = error?.config;
        if (error?.response?.status === 401 && !prevRequest?.sent) {
          prevRequest.sent = true;

          // Retry NIE zadziała dla FormData bo body jest raz-użytkowym strumieniem.
          // Odświeżamy sesję (bez wylogowania, jeśli refresh się uda) i prosimy
          // o ponowne wysłanie zamiast wysyłać pusty request (który backend
          // odrzuci jako "no files provided").
          if (prevRequest.data instanceof FormData) {
            try {
              await refresh();
            } catch {
              logout();
              return Promise.reject(
                new Error("Sesja wygasła podczas wysyłania pliku. Zaloguj się ponownie i spróbuj wgrać plik jeszcze raz."),
              );
            }
            return Promise.reject(
              new Error("Sesja została odświeżona w trakcie wysyłania pliku. Wyślij plik jeszcze raz."),
            );
          }

          try {
            const newAccessToken = await refresh();
            prevRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            return privateApiClient(prevRequest);
          } catch (error) {
            logout();
            return Promise.reject(error);
          }
        }

        onError(error);

        return Promise.reject(error);
      },
    );

    const publicApiClientResponseIntercept = publicApiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        onError(error);
        return Promise.reject(error);
      },
    );

    return () => {
      privateApiClient.interceptors.request.eject(privateApiClientRequestIntercept);
      privateApiClient.interceptors.response.eject(privateApiClientResponseIntercept);
      publicApiClient.interceptors.response.eject(publicApiClientResponseIntercept);
    };
  }, [auth, refresh, logout]);

  return { privateApiClient, publicApiClient };
};

export default useApiClient;
